import React from "react";

import { bodyFront } from "./assets/bodyFront";
import { bodyBack } from "./assets/bodyBack";
import { SvgMaleWrapper } from "./components/SvgMaleWrapper";
import { bodyFemaleFront } from "./assets/bodyFemaleFront";
import { bodyFemaleBack } from "./assets/bodyFemaleBack";
import { SvgFemaleWrapper } from "./components/SvgFemaleWrapper";
import { defaultBorder, defaultColor } from "./utils";

export type Slug =
	| "abs"
	| "adductors"
	| "ankles"
	| "biceps"
	| "calves"
	| "chest"
	| "deltoids"
	| "feet"
	| "forearm"
	| "gluteal"
	| "hamstring"
	| "hands"
	| "hair"
	| "head"
	| "knees"
	| "lower-back"
	| "neck"
	| "obliques"
	| "quadriceps"
	| "tibialis"
	| "trapezius"
	| "triceps"
	| "upper-back"
	| "vagina"
	| "penis"
	| "scrotum"
	| "perineum"
	| "gluteal-crease"
	| "anus"
	| "groin"
	| "pelvis"
	| "breasts"
	| "female-abs"
	| "female-gluteal-crease"
	| "female-perineum"
	| "female-anus"
	| "female-vagina"
	| "backbone"
	| "spine";

export interface BodyPart {
	color?: string;
	slug?: Slug;
	path?: {
		common?: string[];
		left?: string[];
		right?: string[];
	};
}

export interface ExtendedBodyPart extends BodyPart {
	intensity?: number;
	leftSideIntensity?: number;
	rightSideIntensity?: number;
	side?: "left" | "right";
}

export type BodyProps = {
	colors?: ReadonlyArray<string>;
	data: ReadonlyArray<ExtendedBodyPart>;
	scale?: number;
	side?: "front" | "back";
	gender?: "male" | "female";
	onBodyPartClick?: (b: ExtendedBodyPart, side?: "left" | "right") => void;
	border?: string | "none";
};

const Body = ({
	colors = [
		"var(--color-brand-500, #422afb)",
		"var(--color-brand-400, #7551ff)",
	],
	data,
	scale = 1,
	side = "front",
	gender = "male",
	onBodyPartClick,
	border = defaultBorder,
}: BodyProps) => {
	const renderBodySvg = (bodyToRender: ReadonlyArray<BodyPart>) => {
		const SvgWrapper = gender === "male" ? SvgMaleWrapper : SvgFemaleWrapper;

		return (
			<SvgWrapper side={side} scale={scale} border={border}>
				{bodyToRender.map((bodyPart: BodyPart) => {
					const commonPaths = (bodyPart.path?.common || []).map((path) => {
						const commonMatch = data.find((d) => d.slug === bodyPart.slug);
						const isCommonSelected = Boolean(commonMatch);
						const fill = isCommonSelected
							? colors[(commonMatch?.intensity || 1) - 1] || colors[0]
							: bodyPart.color || defaultColor;

						return (
							<path
								style={{ cursor: "pointer" }}
								key={path}
								onClick={() => onBodyPartClick?.(bodyPart)}
								id={bodyPart.slug}
								data-side="common"
								fill={fill}
								d={path}
							/>
						);
					});

					const leftPaths = (bodyPart.path?.left || []).map((path) => {
						const leftMatch = data.find(
							(d) =>
								d.slug === bodyPart.slug &&
								(d.side === "left" || (!d.side && !bodyPart.path?.right))
						);
						const isLeftSelected = Boolean(leftMatch);
						const leftIntensity = leftMatch?.leftSideIntensity || leftMatch?.intensity || 1;
						const fill = isLeftSelected
							? colors[leftIntensity - 1] || colors[0]
							: defaultColor;

						return (
							<path
								style={{ cursor: "pointer" }}
								key={path}
								onClick={() => onBodyPartClick?.(bodyPart, "left")}
								id={bodyPart.slug}
								data-side="left"
								fill={fill}
								d={path}
							/>
						);
					});

					const rightPaths = (bodyPart.path?.right || []).map((path) => {
						const rightMatch = data.find(
							(d) =>
								d.slug === bodyPart.slug &&
								(d.side === "right" || (!d.side && !bodyPart.path?.left))
						);
						const isRightSelected = Boolean(rightMatch);
						const rightIntensity = rightMatch?.rightSideIntensity || rightMatch?.intensity || 1;
						const fill = isRightSelected
							? colors[rightIntensity - 1] || colors[0]
							: defaultColor;

						return (
							<path
								style={{ cursor: "pointer" }}
								key={path}
								onClick={() => onBodyPartClick?.(bodyPart, "right")}
								id={bodyPart.slug}
								data-side="right"
								fill={fill}
								d={path}
							/>
						);
					});

					return [...commonPaths, ...leftPaths, ...rightPaths];
				})}
			</SvgWrapper>
		);
	};

	if (gender === "female") {
		return renderBodySvg(side === "front" ? bodyFemaleFront : bodyFemaleBack);
	}

	return renderBodySvg(side === "front" ? bodyFront : bodyBack);
};

export default Body;
